/**
 * Options REST API
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type { AccountFundingFlowResponse, OptionMarginAccountInformationResponse } from '../types';

/**
 * AccountApi - axios parameter creator
 */
const AccountApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Query account funding flows.
         *
         * Weight(IP): 1
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Only support querying data in the past 3 months
         *
         * @summary Account Funding Flow (USER_DATA)
         * @param {AccountFundingFlowCurrencyEnum} currency Asset type, only support USDT  as of now
         * @param {number | bigint} [recordId] Return the recordId and subsequent data, the latest data is returned by default
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        accountFundingFlow: async (
            currency: AccountFundingFlowCurrencyEnum,
            recordId?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'currency' is not null or undefined
            assertParamExists('accountFundingFlow', 'currency', currency);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (currency !== undefined && currency !== null) {
                localVarQueryParameter['currency'] = currency;
            }
            if (recordId !== undefined && recordId !== null) {
                localVarQueryParameter['recordId'] = recordId;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/bill',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get current account information.
         *
         * Weight(IP): 3
         *
         * Security Type: USER_DATA
         *
         * @summary Option Margin Account Information (USER_DATA)
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        optionMarginAccountInformation: async (
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/marginAccount',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * AccountApi - interface
 * @interface AccountApi
 */
export interface AccountApiInterface {
    /**
     * Query account funding flows.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Only support querying data in the past 3 months
     *
     * @summary Account Funding Flow (USER_DATA)
     * @param {AccountFundingFlowRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    accountFundingFlow(
        requestParameters: AccountFundingFlowRequest
    ): Promise<RestApiResponse<AccountFundingFlowResponse>>;
    /**
     * Get current account information.
     *
     * Weight(IP): 3
     *
     * Security Type: USER_DATA
     *
     * @summary Option Margin Account Information (USER_DATA)
     * @param {OptionMarginAccountInformationRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    optionMarginAccountInformation(
        requestParameters?: OptionMarginAccountInformationRequest
    ): Promise<RestApiResponse<OptionMarginAccountInformationResponse>>;
}

/**
 * Request parameters for accountFundingFlow operation in AccountApi.
 * @interface AccountFundingFlowRequest
 */
export interface AccountFundingFlowRequest {
    /**
     * Asset type, only support USDT  as of now
     * @type {'USDT'}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly currency: AccountFundingFlowCurrencyEnum;

    /**
     * Return the recordId and subsequent data, the latest data is returned by default
     * @type {number | bigint}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly recordId?: number | bigint;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned
     * @type {number | bigint}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly limit?: number | bigint;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof AccountApiAccountFundingFlow
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for optionMarginAccountInformation operation in AccountApi.
 * @interface OptionMarginAccountInformationRequest
 */
export interface OptionMarginAccountInformationRequest {
    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof AccountApiOptionMarginAccountInformation
     */
    readonly recvWindow?: number | bigint;
}

/**
 * AccountApi - object-oriented interface
 * @class AccountApi
 */
export class AccountApi implements AccountApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = AccountApiAxiosParamCreator(configuration);
    }

    /**
     * Query account funding flows.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Only support querying data in the past 3 months
     *
     * @summary Account Funding Flow (USER_DATA)
     * @param {AccountFundingFlowRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<AccountFundingFlowResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/account#account-funding-flow Binance API Documentation}
     */
    public async accountFundingFlow(
        requestParameters: AccountFundingFlowRequest
    ): Promise<RestApiResponse<AccountFundingFlowResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.accountFundingFlow(
            requestParameters?.currency,
            requestParameters?.recordId,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit,
            requestParameters?.recvWindow
        );
        return sendRequest<AccountFundingFlowResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get current account information.
     *
     * Weight(IP): 3
     *
     * Security Type: USER_DATA
     *
     * @summary Option Margin Account Information (USER_DATA)
     * @param {OptionMarginAccountInformationRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OptionMarginAccountInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/account#option-margin-account-information Binance API Documentation}
     */
    public async optionMarginAccountInformation(
        requestParameters: OptionMarginAccountInformationRequest = {}
    ): Promise<RestApiResponse<OptionMarginAccountInformationResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.optionMarginAccountInformation(
                requestParameters?.recvWindow
            );
        return sendRequest<OptionMarginAccountInformationResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}

export enum AccountFundingFlowCurrencyEnum {
    USDT = 'USDT',
}
